'use client';
import React from 'react';
import sanitizeHtml from 'sanitize-html';
import IndexBasedStepSelector from '../shared/IndexBasedStepSelector';
import ColorBox from '../shared/ColorBox';
import { AnimatePresence, motion } from 'framer-motion';
import { useIndexSelection } from '@/hooks/useIndexSelection';

const sanitizeAndRenderHtml = (
  content: string | undefined
): { __html: string } | undefined => {
  if (!content) return undefined;

  const sanitized = sanitizeHtml(content, {
    allowedAttributes: {
      span: ['class'],
    },
    allowedClasses: {
      span: ['*'], // Allow all classes on span elements
    },
    allowedTags: ['b', 'strong', 'i', 'em', 'u', 'span', 'br', 'p'],
  });

  return { __html: sanitized };
};

interface Step {
  label: string;
  description: string;
  buttonText?: string;
  buttonHref?: string;
}

const HowItWorksSection = ({
  sectionHeading = 'How It Works',
  steps = [
    {
      description:
        "Purchase a kit through the trusti™ website or grab one at one of our events. Make an account while you're at it (we promise it's painless!).",
      label: 'Order a Kit',
    },
    {
      description:
        "Once you have your kit, follow our easy instructions to complete your test. Our nurses are available if you need a hand - it's included, so use them. Trust us, they've seen it all.",
      label: 'Complete Your Test',
    },
    {
      description:
        'Your test results are private, and you are not required to disclose them. Regardless of whether you choose to share your results or not, you can book a follow-up appointment. Our goal is to get you the care you need. Reporting your results helps our nurses know what resources you will need before your follow-up appointment.\n\nYour health data is stored securely using a ISO27001-certified system, the global standard for information security.',
      label: 'Interpret Your Results',
    },
    {
      buttonHref: '/sign-in',
      buttonText: 'Get Support',
      description:
        "Need to talk? Our team of nurses is ready to support you. They're here to break down your results, point you to treatment if needed, or answer those sexual health questions that have been keeping you up since 3am.\n\nTo book a follow-up appointment, sign into your account home-page or follow the prompts in the test portal to schedule a chat. First time? You can pre-book a nurse after you purchase your kit.",
      label: 'Get Support',
    },
  ],
}: {
  sectionHeading?: string;
  steps?: Step[];
}) => {
  const howItWorkSteps = React.useMemo(
    () =>
      steps.map(step => ({
        buttonHref: step.buttonHref,
        buttonText: step.buttonText,
        description: (
          <span
            dangerouslySetInnerHTML={sanitizeAndRenderHtml(
              step.description.replace(/\n/g, '<br />')
            )}
          />
        ),
        label: step.label,
      })),
    [steps]
  );

  const [direction, setDirection] = React.useState(0);
  const { selectedIndex, selectedItem, setSelectedIndex } =
    useIndexSelection(howItWorkSteps);

  const handleSelect = (index: number) => {
    setDirection(index > selectedIndex ? 1 : -1);
    setSelectedIndex(index);
  };

  const slideVariants = {
    center: {
      opacity: 1,
      x: 0,
    },
    enter: (dir: number) => ({
      opacity: 0,
      x: dir > 0 ? 300 : -300,
    }),
    exit: (dir: number) => ({
      opacity: 0,
      x: dir > 0 ? -300 : 300,
    }),
  };

  return (
    <section className="my-4 flex items-center justify-center p-0 sm:px-8 sm:py-[48px]">
      <div className="content-wrapper flex w-full items-center justify-center">
        <div className="flex w-full flex-col items-center">
          <div>
            <h2
              className="mb-[45px] text-center font-['Work_Sans'] text-[16px] leading-[25px] font-medium tracking-[2%] uppercase"
              dangerouslySetInnerHTML={sanitizeAndRenderHtml(sectionHeading)}
            />
          </div>

          <div className="relative w-full overflow-hidden sm:max-w-[530px]">
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={selectedIndex}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.3, ease: 'easeInOut' }}
              >
                <ColorBox
                  title={selectedItem?.label || ''}
                  description={selectedItem?.description}
                  buttonText={selectedItem?.buttonText}
                  buttonHref={selectedItem?.buttonHref}
                />
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="w-full">
            <div className="flex w-full flex-col items-center justify-center px-6 sm:flex-row sm:px-0">
              <IndexBasedStepSelector
                items={howItWorkSteps}
                selectedIndex={selectedIndex}
                setSelectedIndex={handleSelect}
                getItemLabel={step => step.label}
                theme="howItWorks"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
